import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, addDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import zxcvbn from "zxcvbn";
import * as bcrypt from "bcryptjs";
import parsePhoneNumber from "libphonenumber-js/max";
import { z } from "zod";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | number | Date) {
  if (!date) return "";
  if (typeof date === "string") {
    date = new Date(date);
  }
  return format(date, "PPP");
}

export function getInitials(name?: string) {
  if (!name) return "";

  const [first, last] = name.split(" ");

  // Return the first letter of the first and last name only if the name exists
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`;
}

/**
 * Formats a duration given in seconds into a human-readable string.
 *
 * @param {number} duration - The duration to format, in seconds.
 * @param {boolean} [long=false] - Whether to use long format (e.g., "2 days, 3 hours and 4 minutes") or short format (e.g., "2d, 3h and 4m").
 * @returns {string} The formatted duration string.
 */
export function formatDuration(duration: number, long?: boolean) {
  const days = Math.floor(duration / (60 * 60 * 24));
  const hours = Math.floor((duration % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((duration % (60 * 60)) / 60);

  const daysPart =
    days > 0
      ? `${days}${long ? " days" : "d"}${hours > 0 || minutes > 0 ? ", " : ""}`
      : "";

  const hoursPart =
    hours > 0
      ? `${hours}${long ? " hours" : "h"}${minutes > 0 ? " and " : ""}`
      : "";

  const minutesPart = minutes > 0 ? `${minutes}${long ? " minutes" : "m"}` : "";

  return `${daysPart}${hoursPart}${minutesPart}` || "0m";
}

export const debounce = <T extends any[]>(
  fn: (...args: T) => void,
  delay: number,
) => {
  let timeoutId: NodeJS.Timeout | null;
  return function (...args: T) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

// Get age from birthday
export const getAge = (birthday: string) => {
  const today = new Date();
  const birthDate = new Date(birthday);

  // Return '-' if birthdate is in the future or invalid
  if (birthDate > today) {
    return "-";
  }

  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  // Return '-' if age calculation results in 0 or negative
  return age <= 0 ? "-" : age;
};

export const sanitizePhoneNumber = (input: string) => {
  return input.replace(/\D/g, "");
};

// Replace the EMAIL_REGEX constant with a Zod schema
const emailSchema = z.string().email("Please enter a valid email address");

export interface FormValidationResult {
  isValid: boolean;
  error?: string;
}

export function validateEmail(email: string): FormValidationResult {
  const result = emailSchema.safeParse(email);

  if (!result.success) {
    return {
      isValid: false,
      error: result.error.errors[0]?.message || "Email is required",
    };
  }

  return { isValid: true };
}

export function validatePhoneNumber(phoneNumber: string): FormValidationResult {
  if (!phoneNumber) {
    return { isValid: false, error: "Phone number is required" };
  }

  let inputPhone = phoneNumber as string;

  if (!inputPhone?.startsWith("+")) {
    inputPhone =
      inputPhone?.length === 10 ? `+1${inputPhone}` : `+${inputPhone}`;
  }

  const parsedPhoneNumber = parsePhoneNumber(inputPhone);

  if (!parsedPhoneNumber?.isValid()) {
    return { isValid: false, error: "Please enter a valid phone number" };
  }

  return { isValid: true };
}

export function validatePassword(
  password: string,
  confirmPassword: string,
  passwordStrength: { score: number } | null,
): FormValidationResult {
  if (!password) {
    return { isValid: false, error: "Password is required" };
  }
  if (!confirmPassword) {
    return { isValid: false, error: "Please confirm your password" };
  }
  if ((passwordStrength?.score ?? 0) < 3) {
    return { isValid: false, error: "Please use a stronger password" };
  }
  if (password !== confirmPassword) {
    return { isValid: false, error: "Passwords do not match" };
  }
  return { isValid: true };
}

/**
 * Password strength levels based on zxcvbn score
 * 0: Very weak
 * 1: Weak
 * 2: Fair
 * 3: Strong
 * 4: Very strong
 */
export const PASSWORD_STRENGTH_LEVELS = {
  0: "Very weak",
  1: "Weak",
  2: "Fair",
  3: "Strong",
  4: "Very strong",
} as const;

/**
 * Checks if a password meets the minimum strength requirement (score >= 3)
 * @param password The password to check
 * @returns Object containing whether the password is strong enough and additional feedback
 */
export function checkPasswordStrength(password: string) {
  const result = zxcvbn(password);

  return {
    isStrongEnough: result.score >= 3,
    score: result.score,
    feedback: result.feedback.warning || result.feedback.suggestions.join(" "),
    crackTime: result.crack_times_seconds.offline_slow_hashing_1e4_per_second,
  };
}

/**
 * Hashes a password using bcrypt
 * @param password The plain text password to hash
 * @returns Promise resolving to an object containing the hash type and hashed value
 */
export async function hashPassword(
  password: string,
): Promise<{ hash: string; value: string }> {
  const salt = await bcrypt.genSalt(12); // Using 12 rounds for a good balance of security and performance
  const hashedPassword = await bcrypt.hash(password, salt);

  return {
    hash: "bcrypt",
    value: hashedPassword,
  };
}

export interface ScheduleDay {
  day: string;
  isOnline: boolean;
  startTime: string;
  endTime: string;
}

export interface ScheduleData {
  schedule: ScheduleDay[];
  timezone: string;
}

export function getCurrentDayName(): string {
  return new Date().toLocaleDateString('en-US', { weekday: 'long' });
}


export function isCurrentlyOnline(scheduleData: ScheduleData, viewerTimezone?: string): boolean {
  const now = new Date();
  const mentorTimezone = scheduleData.timezone;
  
  try {
    if (viewerTimezone) {
      try {
        formatInTimeZone(now, viewerTimezone, 'EEEE');
      } catch (error) {
        return false;
      }
    }
    
    const currentDay = formatInTimeZone(now, mentorTimezone, 'EEEE');
    const currentTimeStr = formatInTimeZone(now, mentorTimezone, 'HH:mm');
    
    const todaySchedule = scheduleData.schedule.find(day => day.day === currentDay);
    if (!todaySchedule || !todaySchedule.isOnline) {
      return false;
    }
    
    return currentTimeStr >= todaySchedule.startTime && currentTimeStr <= todaySchedule.endTime;
  } catch (error) {
    console.error('Error checking online status:', error);
    return false;
  }
}

export function getNextAvailability(scheduleData: ScheduleData, viewerTimezone?: string): string | null {
  const mentorTimezone = scheduleData.timezone;
  const displayTimezone = viewerTimezone || mentorTimezone;
  
  try {
    const now = new Date();
    
    for (let i = 0; i < 7; i++) {
      const checkDate = addDays(now, i);
      
      const dayName = formatInTimeZone(checkDate, mentorTimezone, 'EEEE');
      
      const daySchedule = scheduleData.schedule.find(day => day.day === dayName);
      if (daySchedule && daySchedule.isOnline) {
        const currentTimeInMentorTz = formatInTimeZone(now, mentorTimezone, 'HH:mm');
        
        if (i === 0) {
          if (daySchedule.startTime > currentTimeInMentorTz) {
            const mentorDateStr = formatInTimeZone(checkDate, mentorTimezone, 'yyyy-MM-dd');
            const timezoneOffset = formatInTimeZone(checkDate, mentorTimezone, 'xxx');
            const isoString = `${mentorDateStr}T${daySchedule.startTime}:00${timezoneOffset}`;
            const scheduleDateTime = new Date(isoString);
            
            const displayTime = formatInTimeZone(scheduleDateTime, displayTimezone, 'h:mm a');
            return `${dayName} at ${displayTime}`;
          }
        } else {
          const mentorDateStr = formatInTimeZone(checkDate, mentorTimezone, 'yyyy-MM-dd');
          const timezoneOffset = formatInTimeZone(checkDate, mentorTimezone, 'xxx');
          const isoString = `${mentorDateStr}T${daySchedule.startTime}:00${timezoneOffset}`;
          const scheduleDateTime = new Date(isoString);
          
          const displayTime = formatInTimeZone(scheduleDateTime, displayTimezone, 'h:mm a');
          return `${dayName} at ${displayTime}`;
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting next availability:', error);
    return null;
  }
}

export function formatAvailabilityText(scheduleData: ScheduleData, viewerTimezone?: string): string {
  const isOnline = isCurrentlyOnline(scheduleData, viewerTimezone);
  
  if (isOnline) {
    return "Your mentor is online now";
  }
  
  const nextAvailable = getNextAvailability(scheduleData, viewerTimezone);
  if (nextAvailable) {
    return `Your mentor will be online ${nextAvailable}`;
  }
  
  return "Your mentor is currently offline";
}

// Helper function to calculate days difference from today
export const getDaysAgo = (dateString: string): number => {
  if (!dateString) return 0;

  const inputDate = new Date(dateString);
  const today = new Date();

  // Set both dates to midnight to compare calendar dates only
  inputDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - inputDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export function formatTimeTo12Hour(time24: string): string {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

export function formatTimeFrom12Hour(time12: string): string {
  const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return time12;
  
  const [, hoursStr, minutes, period] = match;
  let hours = parseInt(hoursStr, 10);
  
  if (period.toUpperCase() === 'PM' && hours !== 12) {
    hours += 12;
  } else if (period.toUpperCase() === 'AM' && hours === 12) {
    hours = 0;
  }
  
  return `${hours.toString().padStart(2, '0')}:${minutes}`;
}
